import { FC, useState, useEffect, useCallback, ChangeEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useForm, SubmitHandler, UseFormSetValue } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import { APP_ROUTES, AUTH_ROUTES } from '@navigation/types';
import { isErrorWithStringMessage, ErrorType } from '@redux/baseQuery';
import { useLoginMutation } from '@redux/queries/auth';
import { useAuthUser } from '@redux/selectors/auth';
import theme from 'theme';
import { email, password } from '@utils/constants/schemas';
import {
  TEXT_INPUT_PLACEHOLDERS,
  TEXT_INPUT_LABELS,
  TEXT_INPUT_ICONS,
} from '@utils/constants/form';
import { getRedirectPath, clearRedirectPath } from '@utils/redirect';
import TextInput from '@components/fields/input';
import PasswordInput from '@components/fields/input/PasswordInput';

import {
  StyledContainer,
  StyledFormContainer,
  StyledForm,
  StyledButton,
  StyledErrorContainer,
  StyledErrorText,
} from './styled';

const ForgotPasswordLink = styled(Link)`
  align-self: flex-start;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.purple[300]};
  text-decoration: none;
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

type LoginState = {
  email: string;
  password: string;
};

const defaultValues: LoginState = {
  email: '',
  password: '',
};

const schema = yup.object().shape({
  email,
  password,
});

const DEFAULT_ERROR_MESSAGE = 'Invalid email or password';

export const LogInForm: FC = () => {
  const [login, { isSuccess, isLoading, isError, error }] = useLoginMutation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user: authUser } = useAuthUser();
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset: resetForm,
    watch,
    formState: { errors },
  } = useForm<LoginState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = useCallback(
    ({
      field,
      text,
      onChange,
    }: {
      field: keyof LoginState;
      text: string;
      onChange: UseFormSetValue<LoginState>;
    }) => {
      clearErrors(field);
      onChange(field, text);

      if (errorMessage) {
        setErrorMessage('');
      }
    },
    [clearErrors, errorMessage, setErrorMessage],
  );

  const onSubmit: SubmitHandler<LoginState> = (data: LoginState) => {
    if (data) {
      login(data);
    }

    resetForm({}, { keepValues: true, keepDirty: false });
  };

  useEffect(() => {
    if (isSuccess) {
      const from =
        (location.state as { from?: string })?.from || getRedirectPath();

      if (authUser && !authUser?.emailVerified) {
        navigate(AUTH_ROUTES.CONFIRM_EMAIL, { replace: true });
      } else if (authUser && authUser?.emailVerified && !authUser?.username) {
        navigate(AUTH_ROUTES.ONBOARDING, { replace: true });
      } else if (authUser && authUser?.emailVerified && authUser?.username) {
        clearRedirectPath();
        navigate(from || APP_ROUTES.HOME, { replace: true });
      }
    }
  }, [isSuccess, navigate, authUser, location.state]);

  useEffect(() => {
    if (isError) {
      setErrorMessage(
        isErrorWithStringMessage(error)
          ? (error as ErrorType)?.data?.message || DEFAULT_ERROR_MESSAGE
          : DEFAULT_ERROR_MESSAGE,
      );
    }
  }, [isError, error]);

  return (
    <StyledContainer>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <StyledFormContainer>
          <TextInput
            placeholder={TEXT_INPUT_PLACEHOLDERS.EMAIL}
            label={TEXT_INPUT_LABELS.EMAIL}
            icon={TEXT_INPUT_ICONS.EMAIL}
            errorText={errors.email?.message}
            {...register('email')}
            value={watch('email')}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange({
                field: 'email',
                text: e.target.value,
                onChange: setValue,
              })
            }
          />

          <PasswordInput
            placeholder={TEXT_INPUT_PLACEHOLDERS.PASSWORD}
            label={TEXT_INPUT_LABELS.PASSWORD}
            errorText={errors.password?.message}
            {...register('password')}
            value={watch('password')}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange({
                field: 'password',
                text: e.target.value,
                onChange: setValue,
              })
            }
          />
        </StyledFormContainer>

        <ForgotPasswordLink to={AUTH_ROUTES.FORGOT_PASSWORD}>
          Forgot your password?
        </ForgotPasswordLink>

        <StyledErrorContainer>
          {errorMessage && <StyledErrorText>{errorMessage}</StyledErrorText>}
        </StyledErrorContainer>

        <StyledButton
          disabled={isLoading || !watch('email') || !watch('password')}
          isLoading={isLoading}
          type='submit'
          textStyle={{ fontSize: theme.fonts.fontSize.xl }}
        >
          Log In
        </StyledButton>
      </StyledForm>
    </StyledContainer>
  );
};
